import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import { Table, Row, Col, LoadingLine, ErrorBoundary } from '@spoiler-alert/ui-library';
import NewButton from '../import-manager/new-button';
import ExportMapping from './export-mapping';
import { moveRow, nextOrder } from './row-order';

const styles = {
  tableContainer: {
    position: 'relative',
    width: '100%',
  },
  newCol: {},
  table: {
    '& tr>td': {
      padding: 8,
      '&$newCol': {
        padding: [15, 8, 17, 24],
      },
    },
    marginBottom: '150px',
  },
};

const toEditingMappings = (mappings) =>
  mappings.map((mapping) => {
    const headerStyles = { ...mapping.headerStyles } || {};
    const columnStyles = { ...mapping.columnStyles } || {};
    if (!mapping.headerStyles?.color) headerStyles.color = 'Smoke';
    if (!mapping.columnStyles?.color) columnStyles.color = 'Snow';
    return { ...mapping, headerStyles, columnStyles };
  });

const ExportMappingsTable = ({
  classes,
  exportProfile,
  mutations,
  mappings,
  possibleInternalFields,
  possibleFormatters,
  possibleColors,
  updatedMapping,
  loading,
}) => {
  const [editingMappings, setEditingMappings] = useState(() => toEditingMappings(mappings));
  const savingOrder = useRef(false);

  useEffect(() => {
    setEditingMappings(toEditingMappings(mappings));
  }, [mappings]);

  const updateMapping = (exportMapping) => {
    // update all fields except order
    delete exportMapping.order;
    mutations.updateExportMapping({
      variables: { exportMapping },
    });
  };

  const createMapping = (exportMapping) => {
    exportMapping.exportProfileId = exportProfile._id;
    exportMapping.order = nextOrder(editingMappings);
    mutations.createExportMapping({ variables: { exportMapping } });
  };

  const createNewMapping = () => createMapping({});

  const deleteMapping = (exportMapping) => {
    mutations.deleteExportMapping({
      variables: { exportMapping },
    });
  };

  const moveMapping = (from, to) => {
    // Each save sends every row's order, so with two in flight the earlier one could land last
    // and undo the later move. Clicks made while a save is pending are ignored instead.
    if (savingOrder.current) return;
    savingOrder.current = true;

    const moved = moveRow(editingMappings, from, to);
    // Shown before the server answers; put back as the server last returned it if the save fails.
    setEditingMappings(moved);
    mutations
      .updateMappingsOrder({
        variables: {
          rows: { exportProfileId: exportProfile._id, rowsData: moved.map(({ _id, order }) => ({ _id, order })) },
        },
      })
      // The error itself is shown by the page, from the mutation's own error state.
      .catch(() => setEditingMappings(toEditingMappings(mappings)))
      .finally(() => {
        savingOrder.current = false;
      });
  };

  const onUp = (i) => moveMapping(i, i - 1);

  const onDown = (i) => moveMapping(i, i + 1);

  return (
    <ErrorBoundary>
      <div className={classes.tableContainer}>
        <LoadingLine loading={loading} />
        <Table className={classes.table}>
          {[
            <Row key="first row" header>
              <Col />
              <Col header>Label</Col>
              <Col header>Cell type</Col>
              <Col header>Column width</Col>
              <Col header>Internal name</Col>
              <Col header>Formatter</Col>
              <Col header>Argument</Col>
              <Col header>Formula</Col>
              <Col header>Colors</Col>
              <Col header />
            </Row>,
            ...editingMappings.map((m, i) => (
              <ExportMapping
                required
                key={m._id}
                mapping={m}
                possibleInternalFields={possibleInternalFields}
                possibleFormatters={possibleFormatters}
                possibleColors={possibleColors}
                deleteMapping={deleteMapping}
                updateMapping={updateMapping}
                onUp={onUp}
                onDown={onDown}
                position={i}
                first={i === 0}
                last={i === editingMappings.length - 1}
                justSaved={m._id === updatedMapping?.updateExportMapping._id}
              />
            )),
            <Row key="last row">
              <Col className={classes.newCol} colspan={9}>
                <NewButton onClick={createNewMapping}>New</NewButton>
              </Col>
            </Row>,
          ]}
        </Table>
      </div>
    </ErrorBoundary>
  );
};

ExportMappingsTable.propTypes = {
  classes: PropTypes.object,
  exportProfile: PropTypes.object,
  mutations: PropTypes.object,
  mappings: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      order: PropTypes.number,
    }),
  ),
  possibleInternalFields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
    }),
  ),
  possibleFormatters: PropTypes.array,
  possibleColors: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      hex: PropTypes.string,
    }),
  ),
  updatedMapping: PropTypes.objectOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
    }),
  ),
  loading: PropTypes.bool,
};

export default injectSheet(styles)(ExportMappingsTable);
